import { lazy, Suspense } from 'react';
import { createBrowserRouter, RouterProvider, Navigate, Outlet } from 'react-router-dom'
import { Center, Spinner } from '@chakra-ui/react';
// Home ('/') is the most commonly visited route and is kept as a regular
// (non-lazy) import so the landing page renders without an extra
// chunk-fetch waterfall. Every other route below is lazy-loaded so its code
// (and any large dependencies it pulls in, e.g. @react-pdf/renderer) is only
// downloaded when a visitor actually navigates to it, instead of bloating
// the initial bundle everyone downloads.
import Home from './pages/home';
const OurBrands = lazy(() => import('./pages/careers/ourbrands'));
const Careers = lazy(() => import('./pages/careers'));
const TermsOfUse = lazy(() => import('./pages/termsofuse'));
const OurHistory = lazy(() => import('./pages/our-company/our-history'));
const StrategyMissionValues = lazy(() => import('./pages/our-company/strategy-mission-values'));
const Leadership = lazy(() => import('./pages/our-company/leadership'));
const CorporateResponsibilityOverview = lazy(() => import('./pages/corporate-responsibility-group'));
const CorporateResponsibilityImpact = lazy(() => import('./pages/corporate-responsibility-group/impact'));
const GlobalCulture = lazy(() => import('./pages/our-company/global-culture'));
const SupplyChain = lazy(() => import('./pages/corporate-responsibility-group/responsible-practices/supplychain'));
const LeveragingInnovation = lazy(() => import('./pages/corporate-responsibility-group/responsible-practices/leveraginginnovation'));
const OurRegions = lazy(() => import('./pages/careers/regions-group'));
const Distribution = lazy(() => import('./pages/careers/distribution'));
const China = lazy(() => import('./pages/careers/china'));
const Wellbeing = lazy(() => import('./pages/our-company/wellbeing-benefits'));
const ElevatingResponsibilty = lazy(() => import('./pages/corporate-responsibility-group/responsible-practices/elevatingresponsibility'));
const CleanWater = lazy(() => import('./pages/corporate-responsibility-group/stories/cleandrinkingwater'));
const TransitionProjects = lazy(() => import('./pages/corporate-responsibility-group/stories/transition-projects'));
const CollegiateOutdoor = lazy(() => import('./pages/corporate-responsibility-group/stories/collegiateoutdoorprogram'));
const Club1938 = lazy(() => import('./pages/corporate-responsibility-group/stories/club1938'));
const RepairMountainHardwear = lazy(() => import('./pages/corporate-responsibility-group/stories/repairatmountainhardwear'));
const Pfas = lazy(() => import('./pages/corporate-responsibility-group/responsible-practices/pfas'));
const EuropeTabPanel = lazy(() => import('./pages/careers/regions-group/europetabpanel'));
const AsiaTabPanel = lazy(() => import('./pages/careers/regions-group/asiatabpanel'));
const NATabPanel = lazy(() => import('./pages/careers/regions-group/natabpanel'));
const ColumbiaTabPanel = lazy(() => import('./pages/careers/ourbrands/columbiatabpanel'));
const MhwTabPanel = lazy(() => import('./pages/careers/ourbrands/mhwtabpanel'));
const SorelTabPanel = lazy(() => import('./pages/careers/ourbrands/soreltabpanel'));
const PranaTabPanel = lazy(() => import('./pages/careers/ourbrands/pranatabpanel'));
const OverviewTabPanel = lazy(() => import('./pages/corporate-responsibility-group/overviewtabpanel'));
const EmpoweringPeopleTab = lazy(() => import('./pages/corporate-responsibility-group/empoweringpeopletabpanel'));
const SustainingPlacesTabPanel = lazy(() => import('./pages/corporate-responsibility-group/sustainingplacestabpanel'));
const ResponsiblePractices = lazy(() => import('./pages/corporate-responsibility-group/responsiblepracticestabpanel'));
const Highlights = lazy(() => import('./pages/corporate-responsibility-group/highlightstabpanel'));
const TwentyTwentyFourImpactReport = lazy(() => import('./pages/corporate-responsibility-group/impact-reports/2024-impact-report'));
const TwentyTwentyFourImpactReportAppendices = lazy(() => import('./pages/corporate-responsibility-group/impact-reports/2024-impact-report/appendices'));
const TwentyTwentyFourImpactReportEmpowringPeople = lazy(() => import('./pages/corporate-responsibility-group/impact-reports/2024-impact-report/empowering-people'));
const TwentyTwentyFourImpactReportSustainingPlaces = lazy(() => import('./pages/corporate-responsibility-group/impact-reports/2024-impact-report/sustaining-places'));
const TwentyTwentyFourImpactReportResponsiblePractices = lazy(() => import('./pages/corporate-responsibility-group/impact-reports/2024-impact-report/responsible-practices'));
const TwentyTwentyFiveImpactReport = lazy(() => import('./pages/corporate-responsibility-group/impact-reports/2025-impact-report'));
const TwentyTwentyFiveImpactReportAppendices = lazy(() => import('./pages/corporate-responsibility-group/impact-reports/2025-impact-report/appendices'));
const TwentyTwentyFiveImpactReportEmpowringPeople = lazy(() => import('./pages/corporate-responsibility-group/impact-reports/2025-impact-report/empowering-people'));
const TwentyTwentyFiveImpactReportSustainingPlaces = lazy(() => import('./pages/corporate-responsibility-group/impact-reports/2025-impact-report/sustaining-places'));
const TwentyTwentyFiveImpactReportResponsiblePractices = lazy(() => import('./pages/corporate-responsibility-group/impact-reports/2025-impact-report/responsible-practices'));
const Privacy = lazy(() => import('./pages/privacy'));
const Cookies = lazy(() => import('./pages/cookies'));
const CareersFaq = lazy(() => import('./pages/careers/faq'));

// The /pdf-preview route is a local-only dev tool used to preview the Impact
// Report before running scripts/generate-impact-report-pdf.js — it never
// needs to run in production. Keeping the import and route registration
// behind this check means @react-pdf/renderer and the PDF section
// components (a large dependency) are excluded from production builds
// entirely, rather than just split into a lazily-loaded chunk.
const isProduction = process.env.NODE_ENV === 'production';
const PDFPreview = isProduction ? null : lazy(() => import('./pages/pdf-preview'));

function RouteFallback() {
  return (
    <Center minH="100vh">
      <Spinner size="xl" />
    </Center>
  );
}

const routes = [
  {
    element: (
      <Suspense fallback={<RouteFallback />}>
        <Outlet />
      </Suspense>
    ),
    children: [
      {
        path: '/',
        element: <Home />
      },
  {
    path: '/careers',
    element: <Careers />
  },
  {
    path: '/careers/our-brands-group',
    element: <OurBrands />
  },
  {
    path: '/careers/our-brands/columbia-careers',
    element: <ColumbiaTabPanel />
  },
  {
    path: '/careers/our-brands/sorel-careers',
    element: <SorelTabPanel />
  },
  {
    path: '/careers/our-brands/mountain-hardwear-careers',
    element: <MhwTabPanel />
  },
  {
    path: '/careers/our-brands/prana-careers',
    element: <PranaTabPanel />
  },
  // {
  //   path: '/careers/our-brands-group/?tab-78baa3e833ecb8b09e4',
  //   element: <Navigate to='/careers/our-brands/columbia-careers' />
  // },
  {
    path: '/careers/our-brands-group/?tab-78baa3e833ecb8b09e4',
    element: <Navigate to='/careers/our-brands/columbia-careers' replace />
  },
  {
    path: '/careers/our-brands-group/?tab-9d37deaf387dfeb99f5',
    element: <Navigate to='/careers/our-brands/sorel-careers' />
  },
  {
    path: '/careers/our-brands-group/?tab-7f9133205962d06a60c',
    element: <Navigate to='/careers/our-brands/mountain-hardwear-careers' />
  },
  {
    path: '/careers/our-brands-group/?tab-05cf9eb2b0103558eb1',
    element: <Navigate to='/careers/our-brands/prana-careers' />
  },
  {
    path: '/termsofuse',
    element: <TermsOfUse />
  },
  {
    path: '/our-company/our-history',
    element: <OurHistory />
  },
  {
    path: '/our-company/strategy-mission-values',
    element: <StrategyMissionValues />
  },
  {
    path: '/our-company/leadership',
    element: <Leadership />,
  },
  {
    path: '/our-company/inclusion-diversity',
    element: <Navigate to='/our-company/global-culture' />
  },
  {
    path: '/our-company/global-culture',
    element: <GlobalCulture />
  },
  {
    path: '/careers/our-brands-group/tab/tabgroupb/sorel',
    element: <Navigate to='/careers/our-brands/sorel-careers' />
  },
  {
    path: '/corporate-responsibility/impact',
    element: <CorporateResponsibilityImpact />
  },
  {
    path: '/corporate-responsibility/impact/stories/clean-drinking-water-program',
    element: <CleanWater />
  },
  {
    path: '/corporate-responsibility/impact/stories/transition-projects',
    element: <TransitionProjects />
  },
  {
    path: '/corporate-responsibility/impact/stories/collegiate-outdoor-recreation-program',
    element: <CollegiateOutdoor />
  },
  {
    path: '/corporate-responsibility/impact/stories/club-1938',
    element: <Club1938 />
  },
  {
    path: '/corporate-responsibility/impact/stories/repair-services-at-mountain-hardwear',
    element: <RepairMountainHardwear />
  },
  {
    path: '/corporate-responsibility/overview',
    element: <OverviewTabPanel />
  },
  {
    path: '/corporate-responsibility/empowering-people',
    element: <EmpoweringPeopleTab />
  },
  {
    path: '/corporate-responsibility/sustaining-places',
    element: <SustainingPlacesTabPanel />
  },
  {
    path: '/corporate-responsibility/responsible-practices',
    element: <ResponsiblePractices />
  },
  {
    path: '/corporate-responsibility/highlights',
    element: <Highlights />
  },
  {
    path: '/corporate-responsibility-group/?tab-7924f1d2e71de065b6f=',
    element: <Navigate to='/corporate-responsibility/overview' />
  },
  {
    path: '/corporate-responsibility-group/?tab-6b450decc1cd295a70a=',
    element: <Navigate to='/corporate-responsibility/empowering-people' />
  },
  {
    path: '/corporate-responsibility-group/?tab-19017f87daea3a103ca=',
    element: <Navigate to='/corporate-responsibility/sustaining-places' />
  },
  {
    path: '/corporate-responsibility-group/?tab-b57f0800d43fff2925c=',
    element: <Navigate to='/corporate-responsibility/responsible-practices' />
  },
  {
    path: '/corporate-responsibility-group/?tab-bd5d2f27b7afd43e2d1=',
    element: <Navigate to='/corporate-responsibility/highlights' />
  },
  {
    path: '/corporate-responsibility-group',
    element: <CorporateResponsibilityOverview />
  },
  {
    path: '/corporate-responsibility-group/responsible-practices/supply-chain',
    element: <SupplyChain />
  },
  {
    path: '/corporate-responsibility-group/responsible-practices/leveraging-innovation',
    element: <LeveragingInnovation />
  },
  {
    path: '/corporate-responsibility-group/responsible-practices/elevating-responsibility',
    element: <ElevatingResponsibilty />
  },
  {
    path: '/corporate-responsibility-group/responsible-practices/pfas',
    element: <Pfas />
  },
  {
    path: '/careers/regions-group',
    element: <OurRegions />
  },
  {
    path: '/careers/regions-group/north-america',
    element: <NATabPanel />
  },
  {
    path: '/careers/regions-group/asia',
    element: <AsiaTabPanel />
  },
  {
    path: '/careers/regions-group/europe',
    element: <EuropeTabPanel />
  },
  {
    path: '/careers/distribution',
    element: <Distribution />
  },
  {
    path: '/china',
    element: <China />
  },
  {
    path: '/our-company/wellbeing-benefits/',
    element: <Wellbeing />
  },
  {
    path: '/corporate-responsibility/impact/2024',
    element: <TwentyTwentyFourImpactReport />
  },
  {
    path: '/corporate-responsibility/impact/2024/appendices',
    element: <TwentyTwentyFourImpactReportAppendices />
  },
  {
    path: '/corporate-responsibility/impact/2024/empowering-people',
    element: <TwentyTwentyFourImpactReportEmpowringPeople />
  },
  {
    path: '/corporate-responsibility/impact/2024/sustaining-places',
    element: <TwentyTwentyFourImpactReportSustainingPlaces />
  },
  {
    path: '/corporate-responsibility/impact/2024/responsible-practices',
    element: <TwentyTwentyFourImpactReportResponsiblePractices />
  },
  {
    path: '/corporate-responsibility/impact/2025',
    element: <TwentyTwentyFiveImpactReport />
  },
  {
    path: '/corporate-responsibility/impact/2025/appendices',
    element: <TwentyTwentyFiveImpactReportAppendices />
  },
  {
    path: '/corporate-responsibility/impact/2025/empowering-people',
    element: <TwentyTwentyFiveImpactReportEmpowringPeople />
  },
  {
    path: '/corporate-responsibility/impact/2025/sustaining-places',
    element: <TwentyTwentyFiveImpactReportSustainingPlaces />
  },
  {
    path: '/corporate-responsibility/impact/2025/responsible-practices',
    element: <TwentyTwentyFiveImpactReportResponsiblePractices />
  },
  {
    path: '/privacy-policy',
    element: <Privacy />
  },
  {
    path: '/cookie-notice',
    element: <Cookies />
  },
      {
        path: '/careers/frequently-asked-questions',
        element: <CareersFaq />
      },
    ]
  }
];

if (!isProduction) {
  routes.push({
    path: '/pdf-preview',
    element: (
      <Suspense fallback={<RouteFallback />}>
        <PDFPreview />
      </Suspense>
    )
  });
}

const router = createBrowserRouter(routes);

function App() {
  return (
    <RouterProvider router={router} />
  );
}

export default App;
